import React, { Fragment, useEffect, useMemo } from 'react';
import { FuelDivider, FuelTypography } from '@frete.com/fuel-react';
import {
  Container,
  Callout,
  ErrorContent,
  SessionDivider,
  WhatsappContainer,
  ErrorDisclaimer,
  ErrorDisclaimerContent,
} from './styled';
import { ErrorListProps, ErrorViewerContentProps } from './types';
import { CONTACT_NUMBER } from '@/constants';
import { useAppContext } from '@/context';
import { SidebarWrapper } from '../SidebarWrapper';
import { WhatsappContact } from '../WhatsappContact';

const ErrorList: React.FC<ErrorListProps> = ({ data: { errors, disclaimers } }) => {
  const errorsList = useMemo(() => {
    const errorsLength = errors.length;
    const showDivider = (index: number) => index + 1 < errorsLength;

    const mappedErrors = errors.map((item, index) => (
      <Fragment key={item}>
        <FuelTypography color="neutral-default" weight="medium" size="body-sm">
          {item}
        </FuelTypography>
        {showDivider(index) && <FuelDivider />}
      </Fragment>
    ));

    if (!!disclaimers.length) {
      const disclaimersList = disclaimers.map((item) => (
        <FuelTypography key={item} color="neutral-default" weight="regular" size="body-sm">
          <ErrorDisclaimer>{item}</ErrorDisclaimer>
        </FuelTypography>
      ));

      mappedErrors.push(
        <ErrorDisclaimerContent key="disclaimers" data-testid="error-disclaimers">
          {disclaimersList}
        </ErrorDisclaimerContent>,
      );
    }
    return mappedErrors;
  }, [errors, disclaimers]);

  return <ErrorContent>{errorsList}</ErrorContent>;
};

const ErrorViewerContent: React.FC<ErrorViewerContentProps> = ({ data, cpf }) => (
  <>
    <Callout
      message={
        data.errors.length
          ? 'Verifique os erros abaixo e faça um novo cadastro.'
          : 'Motivo do erro não encontrado, clique abaixo e faça um novo cadastro.'
      }
      color="orange"
      icon="alert"
      fullWidth
      iconShape
    />
    <ErrorList data={data} />
    <SessionDivider size="large" />
    <FuelTypography color="neutral-strong" weight="bold" size="body-md">
      Dúvidas?
    </FuelTypography>
    <FuelTypography color="neutral-default" weight="medium" size="body-sm">
      Adicione nosso número de WhatsApp ou clique no botão abaixo para abrir o aplicativo e nos enviar uma mensagem.
    </FuelTypography>
    <FuelTypography color="neutral-default" weight="medium" size="body-sm">
      {CONTACT_NUMBER}
    </FuelTypography>
    <WhatsappContainer>
      <WhatsappContact cellPhone={CONTACT_NUMBER} fullWidth={false} type="noUpdateNeeded" cpf={cpf} />
    </WhatsappContainer>
    <FuelTypography color="neutral-default" weight="regular" size="body-sm">
      <em>O horário de atendimento da Fretebras, é de segunda a sexta-feira das 7h às 20h, exceto feriados.</em>
    </FuelTypography>
  </>
);

export const ErrorViewer: React.FC = () => {
  const {
    state: { riskErrorViewer: state, risk },
    dispatch,
  } = useAppContext();

  const onUnmounted = () => {
    dispatch({ action: 'risk_error_viewer_close' });
  };

  const form = useMemo(() => ({ ...risk.data }), [risk.data]);

  useEffect(() => onUnmounted, []);
  const { cpf } = form.trucker;
  const cpfFormatted = cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  return (
    <SidebarWrapper open={state.isOpen}>
      <Container>
        <ErrorViewerContent data={state.data} cpf={cpfFormatted} />
      </Container>
    </SidebarWrapper>
  );
};
